import { request } from '@helpers/fetch';
import { Response } from '@models/response';

export enum ANALYTICS_EVENTS {
  PageView = 'PageView',
  ProductView = 'Product Viewed',
  InitiateCheckout = 'InitiateCheckout',
  CompleteRegistration = 'CompleteRegistration',
  AddToCart = 'AddToCart',
  RemoveFromCart = 'RemoveFromCart',
  Purchase = 'Purchase',
  ViewQuizStep = 'ViewQuizStep',
  SelectQuizAnswer = 'SelectQuizAnswer',
  QuizStarted = 'QuizStarted',
  QuizStepCompleted = 'QuizStepCompleted',
  QuizCompleted = 'QuizCompleted',
  AddPaymentInfo = 'AddPaymentInfo',
  ViewListItem = 'ViewListItem',
}

// Use non-obvious event names for Meta
// https://issues.yournextagency.com/issue/JRN-2628/Set-up-custom-Meta-events-throughout-funnel
export const EVENTS_MAPPING = {
  [ANALYTICS_EVENTS.InitiateCheckout]: 'EVENT_7367', // 'EVENT_IC'
  [ANALYTICS_EVENTS.CompleteRegistration]: 'EVENT_6782', // 'EVENT_CR'
  [ANALYTICS_EVENTS.AddToCart]: 'EVENT_6567', // 'EVENT_AC'
  [ANALYTICS_EVENTS.RemoveFromCart]: 'EVENT_8267', // 'EVENT_RC'
  [ANALYTICS_EVENTS.Purchase]: 'EVENT_8082', // 'EVENT_PR'
  [ANALYTICS_EVENTS.ViewQuizStep]: 'EVENT_8683', // 'EVENT_VS'
  [ANALYTICS_EVENTS.SelectQuizAnswer]: 'EVENT_8365', // 'EVENT_SA'
  [ANALYTICS_EVENTS.AddPaymentInfo]: 'EVENT_6573', // 'EVENT_AI'
  [ANALYTICS_EVENTS.ViewListItem]: 'EVENT_VL', // Google only
};

export interface TrackPageViewPayload {
  $title: string;
  name: string;
  url: string;
}

export interface TrackViewQuizStepPayload extends TrackQuizAnalyticsPayload {
  stepId: number|string
  stepSlug: string
  stepName?: string
  eventId: string
}

export interface TrackSelectQuizAnswerPayload {
  stepId: number|string
  stepValue: string[]
  stepSlug: string
  answerId: (number|string)[]
  eventId: string
}

export interface TrackProductViewPayload {
  productId: number|string
}

export interface TrackQuizAnalyticsPayload {
  quiz_name: string
  quiz_category: string
  quiz_id: string
  eventId: string
}

export interface TrackQuizStepCompletedPayload extends TrackQuizAnalyticsPayload {
  quiz_step: number
  quiz_questions: {
    label: string
    id: string
    slug: string
    answers: string[]
  }[]
}

export interface TrackCustomEventPayload {
  event: string;
  payload?: Record<string, unknown>;
}

export interface IdentifyPayload {
  email?: string
  firstName?: string
  lastName?: string
  name?: string
  phone?: string
  gender?: string
  birthday?: string
  username?: string
  address?: {
    street?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
}

export const getBrowserInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform ?? '',
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    colorDepth: window.screen.colorDepth,
    referrer: document.referrer,
    pageTitle: document.title,
    title: document.title,
    pageHostname: window.location.hostname,
    pageLocation: window.location.href,
    url: window.location.href,
    pagePath: window.location.pathname,
    pageReferrer: document.referrer,
  };
};

export const getDeviceInfo = () => {
  const screenResolution = `${window.screen.width}x${window.screen.height}`;
  const viewportSize = `${
    window.innerWidth || document.documentElement.clientWidth
  }x${window.innerHeight || document.documentElement.clientHeight}`;
  const encoding = document.characterSet || document.charset || '';
  const language = (
    navigator.language
    || navigator.userLanguage
    || ''
  ).toLowerCase();
  const colorDepth = `${window.screen.colorDepth}-bit`;

  return {
    screen_resolution: screenResolution,
    viewport_size: viewportSize,
    encoding: encoding,
    language: language,
    colors: colorDepth,
    platform: navigator.platform,
    user_agent: navigator.userAgent,
  };
};

const getMarketingData = () => {
  const fbc = localStorage.getItem('fbc');
  const fbp = localStorage.getItem('fbp');
  const utmName = localStorage.getItem('utmName') || null;
  const utmSource = localStorage.getItem('utmSource') || null;
  const utmMedium = localStorage.getItem('utmMedium') || null;
  const utmTerm = localStorage.getItem('utmTerm') || null;
  const utmContent = localStorage.getItem('utmContent') || null;
  const efTransactionId = localStorage.getItem('_ef_transaction_id') || null;
  const gclid = localStorage.getItem('gclid') || null;
  const gbraid = localStorage.getItem('gbraid') || null;

  return {
    fbc,
    fbp,
    utmName,
    utmSource,
    utmMedium,
    utmTerm,
    utmContent,
    efTransactionId,
    gclid,
    gbraid,
  };
};

export const apiAnalytics = {
  trackPageView(payload: TrackPageViewPayload) {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.PageView,
      payload,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackProductView(payload: TrackProductViewPayload) {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.ProductView,
      payload,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackViewQuizStep(payload: TrackViewQuizStepPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.ViewQuizStep,
      eventId: payload.eventId,
      payload,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackSelectQuizAnswer(payload: TrackSelectQuizAnswerPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.SelectQuizAnswer,
      eventId: payload.eventId,
      payload,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackQuizStarted(payload: TrackQuizAnalyticsPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.QuizStarted,
      eventId: payload.eventId,
      payload: {
        quiz_name: payload.quiz_name,
        quiz_category: payload.quiz_category,
        quiz_id: payload.quiz_id,
      },
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackQuizStepCompleted(payload: TrackQuizStepCompletedPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.QuizStepCompleted,
      eventId: payload.eventId,
      payload: {
        quiz_name: payload.quiz_name,
        quiz_category: payload.quiz_category,
        quiz_id: payload.quiz_id,
        quiz_step: payload.quiz_step,
        quiz_questions: payload.quiz_questions,
      },
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  trackQuizCompleted(payload: TrackQuizAnalyticsPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: ANALYTICS_EVENTS.QuizCompleted,
      eventId: payload.eventId,
      payload: {
        quiz_name: payload.quiz_name,
        quiz_category: payload.quiz_category,
        quiz_id: payload.quiz_id,
      },
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  track(payload: TrackCustomEventPayload): Promise<Response<any>> {
    return request.post('/api/v1/track', {
      event: payload.event,
      payload: payload.payload ?? {},
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    });
  },

  /**
   * Server-side RudderStack identify call.
   * Fires through Craft → SQS → Lambda → RudderStack HTTP API (/v1/identify).
   * The Craft controller merges server-resolved user data on top of these traits.
   * Uses fetch keepalive so request can finish during page navigation.
   */
  identify(traits: IdentifyPayload): Promise<Response<any>> {
    const payload = {
      traits,
      device: getDeviceInfo(),
      browser: getBrowserInfo(),
      marketing: getMarketingData(),
    };

    // keepalive helps prevent request cancellation when auth flow redirects to /portal
    return request.post('/api/v1/identify', payload, { keepalive: true });
  },
};
